import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCRM } from '../context/CRMContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import { format, isValid } from 'date-fns';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiPlus, FiMessageCircle, FiCheck, FiUser } = FiIcons;

const Messages = () => {
  const { user: currentUser } = useAuth();
  const { users } = useCRM();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isNewMsgModalOpen, setIsNewMsgModalOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  // Helper to get name from user object consistently
  const getUserName = (u) => u?.fullName || u?.full_name || 'Unknown User';

  // Load initial data
  useEffect(() => {
    if (!currentUser) return;
    loadConversations();
    
    // Real-time subscription for new messages
    const channel = supabase
      .channel('table-db-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages_crm_2025',
        },
        (payload) => {
          const msg = payload.new;
          // If message involves current user, refresh list
          if (msg.sender_id === currentUser.id || msg.receiver_id === currentUser.id) {
            loadConversations();
            
            // If message is for the currently open chat, add it
            if (selectedUser && (msg.sender_id === selectedUser.id || msg.receiver_id === selectedUser.id)) {
              setMessages(prev => {
                if (prev.some(m => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
              // Auto-mark as read if we are looking at it
              if (msg.receiver_id === currentUser.id) {
                markAsRead(selectedUser.id);
              }
            }
          }
        }
      )
      .subscribe();

    // Fallback polling every 10 seconds to ensure sync even if Realtime fails
    pollingInterval.current = setInterval(() => {
      loadConversations();
      if (selectedUser) {
        silentRefreshMessages(selectedUser.id);
      }
    }, 10000);

    return () => {
      supabase.removeChannel(channel);
      if (pollingInterval.current) clearInterval(pollingInterval.current);
    };
  }, [selectedUser?.id, currentUser?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    if (!currentUser) return;
    try {
      const { data, error } = await supabase
        .from('messages_crm_2025')
        .select('*')
        .or(`sender_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const convos = {};
      (data || []).forEach(msg => {
        const otherUserId = msg.sender_id === currentUser.id ? msg.receiver_id : msg.sender_id;
        if (!convos[otherUserId]) {
          const otherUser = users.find(u => u.id === otherUserId);
          if (otherUser) {
            convos[otherUserId] = {
              user: otherUser,
              lastMessage: msg,
              unread: msg.receiver_id === currentUser.id && !msg.is_read
            };
          }
        }
      });
      setConversations(Object.values(convos));
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (otherUserId) => {
    await supabase
      .from('messages_crm_2025')
      .update({ is_read: true })
      .eq('sender_id', otherUserId)
      .eq('receiver_id', currentUser.id)
      .eq('is_read', false);
  };

  const silentRefreshMessages = async (userId) => {
    const { data } = await supabase
      .from('messages_crm_2025')
      .select('*')
      .or(`and(sender_id.eq.${currentUser.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${currentUser.id})`)
      .order('created_at', { ascending: true });
    
    if (data) {
      setMessages(data);
    }
  };

  const loadMessagesWithUser = async (user) => {
    if (!user || !currentUser) return;
    setSelectedUser(user);
    try {
      const { data, error } = await supabase
        .from('messages_crm_2025')
        .select('*')
        .or(`and(sender_id.eq.${currentUser.id},receiver_id.eq.${user.id}),and(sender_id.eq.${user.id},receiver_id.eq.${currentUser.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      await markAsRead(user.id);
      loadConversations();
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!newMessage.trim() || !selectedUser || !currentUser || sending) return;

    setSending(true);
    const msgText = newMessage.trim();
    setNewMessage('');

    // Optimistic Update
    const tempId = Math.random().toString();
    const optimisticMsg = {
      id: tempId,
      sender_id: currentUser.id,
      receiver_id: selectedUser.id,
      content: msgText,
      created_at: new Date().toISOString(),
      is_read: false,
      isOptimistic: true
    };
    
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      const { data, error } = await supabase
        .from('messages_crm_2025')
        .insert([{
          sender_id: currentUser.id,
          receiver_id: selectedUser.id,
          content: msgText
        }])
        .select()
        .single();

      if (error) throw error;
      
      setMessages(prev => prev.map(m => m.id === tempId ? data : m));
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(msgText);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDateSafely = (dateStr) => {
    const date = new Date(dateStr);
    return isValid(date) ? format(date, 'HH:mm') : '--:--';
  };

  if (loading) return <div className="flex justify-center p-12"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
        <Button onClick={() => setIsNewMsgModalOpen(true)}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          New Message
        </Button>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Conversations Sidebar */}
        <Card className="w-80 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Recent Chats</h2>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-400 italic text-sm">No active chats.</div>
            ) : (
              conversations.map(convo => (
                <button
                  key={convo.user.id}
                  onClick={() => loadMessagesWithUser(convo.user)}
                  className={`w-full p-4 flex items-start space-x-3 border-b border-gray-50 transition-colors hover:bg-primary-50 ${selectedUser?.id === convo.user.id ? 'bg-primary-50 border-r-4 border-r-primary-600' : ''}`}
                >
                  <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {getUserName(convo.user).charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex justify-between items-baseline">
                      <p className={`text-sm font-semibold truncate ${convo.unread ? 'text-primary-900' : 'text-gray-900'}`}>
                        {getUserName(convo.user)}
                      </p>
                      <span className="text-[10px] text-gray-400">
                        {formatDateSafely(convo.lastMessage.created_at)}
                      </span>
                    </div>
                    <p className={`text-xs truncate ${convo.unread ? 'text-primary-600 font-bold' : 'text-gray-500'}`}>
                      {convo.lastMessage.sender_id === currentUser.id ? 'You: ' : ''}{convo.lastMessage.content}
                    </p>
                  </div>
                  {convo.unread && (
                    <div className="h-2 w-2 bg-primary-600 rounded-full mt-2"></div>
                  )}
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Chat window */}
        <Card className="flex-1 flex flex-col overflow-hidden bg-gray-50/30">
          {selectedUser ? (
            <>
              <div className="p-4 border-b border-gray-100 bg-white flex items-center">
                <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  {getUserName(selectedUser).charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{getUserName(selectedUser)}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">{selectedUser.role}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                {messages.map(msg => {
                  const isMine = msg.sender_id === currentUser.id;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${isMine ? 'bg-primary-600 text-white rounded-tr-none' : 'bg-white text-gray-900 rounded-tl-none border border-gray-100'}`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className={`text-[9px] mt-1 flex items-center ${isMine ? 'text-primary-100' : 'text-gray-400'}`}>
                          {formatDateSafely(msg.created_at)}
                          {isMine && !msg.isOptimistic && <SafeIcon icon={FiCheck} className="ml-1 h-3 w-3" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 bg-white border-t border-gray-100">
                <div className="flex items-end space-x-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message... (Shift+Enter for new line)"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none resize-none max-h-32 min-h-[44px]"
                    rows={1}
                    style={{ height: 'auto' }}
                    disabled={sending}
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!newMessage.trim() || sending}
                    className="p-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    <SafeIcon icon={FiSend} className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <SafeIcon icon={FiMessageCircle} className="h-12 w-12 mb-2 opacity-20" />
              <p className="text-sm">Select a colleague to start messaging</p>
            </div>
          )}
        </Card>
      </div>

      {/* New Message Modal */}
      <Modal isOpen={isNewMsgModalOpen} onClose={() => setIsNewMsgModalOpen(false)} title="New Message">
        <div className="space-y-4">
          <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Select Team Member</p>
          <div className="max-h-64 overflow-y-auto space-y-1 custom-scrollbar">
            {users
              .filter(u => u.id !== currentUser.id && (u.is_active || u.isActive))
              .map(u => (
                <button
                  key={u.id}
                  onClick={() => {
                    setIsNewMsgModalOpen(false);
                    loadMessagesWithUser(u);
                  }}
                  className="w-full p-3 flex items-center space-x-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent"
                >
                  <div className="h-9 w-9 bg-primary-100 rounded-full flex items-center justify-center font-bold text-primary-700 text-sm">
                    {getUserName(u).charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{getUserName(u)}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{u.role}</p>
                  </div>
                </button>
              ))}
          </div>
        </div>
      </Modal>
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default Messages;